'use strict';

/**
 * Client-side electronic invoicing address (routing key) validation.
 *
 * Deliberate mirror of the server helper
 * `app_storefront_hf/cartridge/scripts/helpers/invoicingElectronicAddressHelper.js`:
 * identical logic kept in sync by hand so the storefront gives immediate, network-free
 * feedback (no ajax round-trip) while the server keeps the authoritative check.
 * Both files are pure JS (no `dw/*`), so the two stay byte-for-byte aligned — any change
 * here MUST be applied to the server twin (and its unit test), and vice versa.
 *
 * See the server twin for the surface rules and the characters left through on purpose.
 */

var FORMAT_PATTERN = /^[^@;:/\\&%#*+=éèëêïîùàâµ\s]+\s*$/;

/**
 * Normalizes a raw routing key: removal of leading and trailing whitespace.
 * @param {string} value - Raw routing key as entered by the user.
 * @returns {string} Normalized routing key (may be empty).
 */
function normalize(value) {
    return String(value || '').trim();
}

/**
 * Checks the surface format of a normalized routing key.
 * @param {string} normalized - Normalized routing key (no surrounding whitespace).
 * @returns {boolean} True when the value holds none of the forbidden characters.
 */
function hasValidFormat(normalized) {
    return FORMAT_PATTERN.test(normalized);
}

/**
 * Validates an electronic invoicing address.
 * @param {string} rawAddress - Raw routing key as entered by the user.
 * @returns {{valid: boolean, normalized: string, reason: string}} Validation result.
 *  `reason` is one of: 'empty', 'format', '' (valid).
 */
function validate(rawAddress) {
    var normalized = normalize(rawAddress);

    if (!normalized) {
        return { valid: false, normalized: '', reason: 'empty' };
    }
    if (!hasValidFormat(normalized)) {
        return { valid: false, normalized: normalized, reason: 'format' };
    }

    return { valid: true, normalized: normalized, reason: '' };
}

module.exports = {
    normalize: normalize,
    hasValidFormat: hasValidFormat,
    validate: validate
};
